/**
 * FlexibleTextSection — Title-only text editor for FlexibleLayout Text sections
 *
 * Renders a single V2TemplateTextElement (title) with full style + template support
 * inside an SAccordion. Supports @links, <tag>labels</tag>, @countdowns,
 * and @paywall variables (when screenType = 'FlexibleLayout').
 *
 * Unlike V2CopyBlockSection which shows title + subtitle + detail, this shows only title.
 *
 * Data flow through proxy adapter:
 *   onChange('content.copy.title.translations.en', 'Hi')
 *   → adapter strips 'content.' → matches prefix 'copy' → innerPath 'title.translations.en'
 *   → deep-clones section.textContent, setNestedValue, writes whole sections array
 */

import React, { useCallback, useState, useMemo } from 'react';
import { SAccordion } from '@modules/ui-components';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { V2TemplateTextElement } from '../../shared/templateFields/V2TemplateTextElement';
import { EditorGuideProvider } from '../../../contexts';
import { V2_FLEXIBLE_TITLE_CONFIG } from '../../../config/v2FlexibleTextConfig';
import { withScreenEditorFieldGuide } from '../../../config/screenEditorFieldGuideResolver';
import { SECTION_MARGIN_BOTTOM } from '../../../config/ui';
import { isFieldEnabled } from '../../../utils/v2FieldOperations';
import { getStyleValues as getStyleValuesUtil } from '../../../utils/fieldValueOperations';
import { useEditorPresentationProfile } from './PresentationProfileContext';
import {
  getSectionTestId,
  getFieldTestId,
  getCheckboxTestId,
  getStylesToggleTestId,
  getInputTestId,
} from '../../../utils/testIdUtils';
import type { FieldConfig } from '../../../types/editorFieldTypes';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';

/** Available parent section for linking */
export interface ParentSectionOption {
  id: string;
  label: string;
  kind: string;
}

export interface FlexibleTextSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  /** Override accordion title (used by FlexibleLayout for section labels) */
  sectionTitle?: string;
  /** Parent screen type for conditional features (e.g., paywall variables) */
  screenType?: string;
  /** Current linked parent section ID */
  parentSectionId?: string;
  /** Available ProductCatalog sections that can provide selected product labels */
  availableParentSections?: ParentSectionOption[];
  /** Callback to change the linked parent section */
  onParentSectionChange?: (parentId: string | undefined) => void;
}

export const FlexibleTextSection: React.FC<FlexibleTextSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  sectionTitle,
  screenType,
  parentSectionId,
  availableParentSections,
  onParentSectionChange,
}) => {
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());

  const toggleFieldExpansion = useCallback((path: string) => {
    setExpandedFields((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const checkFieldEnabled = useCallback(
    (config: FieldConfig): boolean => isFieldEnabled(step, config.path),
    [step],
  );

  const activeProfile = useEditorPresentationProfile();

  const getStyleValues = useCallback(
    (config: FieldConfig): Record<string, unknown> =>
      getStyleValuesUtil(step as unknown as Record<string, unknown>, config, activeProfile),
    [step, activeProfile],
  );

  const handleEnabledChange = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        onChange(V2_FLEXIBLE_TITLE_CONFIG.path, {
          kind: 'static',
          translations: { [locale]: V2_FLEXIBLE_TITLE_CONFIG.defaultValue || '' },
          appearance: {},
        });
      } else {
        onChange(V2_FLEXIBLE_TITLE_CONFIG.path, null);
      }
    },
    [onChange, locale],
  );

  const handleStyleChange = useCallback(
    (stylePath: string, value: unknown) => onChange(stylePath, value),
    [onChange],
  );

  const titleFieldProps = useMemo(
    () => ({
      enabled: checkFieldEnabled(V2_FLEXIBLE_TITLE_CONFIG),
      styleValues: getStyleValues(V2_FLEXIBLE_TITLE_CONFIG),
      expandedStyles: expandedFields.has(V2_FLEXIBLE_TITLE_CONFIG.path),
    }),
    [checkFieldEnabled, getStyleValues, expandedFields],
  );

  const SECTION_NAME = 'Text';

  return (
    <SAccordion
      title={sectionTitle ?? SECTION_NAME}
      collapsible
      defaultExpanded={defaultExpanded}
      sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
      data-testid={getSectionTestId(SECTION_NAME)}
    >
      <EditorGuideProvider>
        <V2TemplateTextElement
          step={step}
          fieldConfig={withScreenEditorFieldGuide(V2_FLEXIBLE_TITLE_CONFIG, 'flexibleSections')}
          locale={locale}
          onChange={onChange}
          {...titleFieldProps}
          onEnabledChange={handleEnabledChange}
          onStylesExpandToggle={() => toggleFieldExpansion(V2_FLEXIBLE_TITLE_CONFIG.path)}
          onStyleChange={handleStyleChange}
          disabled={disabled}
          screenType={screenType}
          data-testid={getFieldTestId(SECTION_NAME, V2_FLEXIBLE_TITLE_CONFIG.label)}
          checkboxTestId={getCheckboxTestId(SECTION_NAME, V2_FLEXIBLE_TITLE_CONFIG.label)}
          stylesToggleTestId={getStylesToggleTestId(SECTION_NAME, V2_FLEXIBLE_TITLE_CONFIG.label)}
          inputTestId={getInputTestId(SECTION_NAME, V2_FLEXIBLE_TITLE_CONFIG.label)}
        />
      </EditorGuideProvider>
      {availableParentSections && availableParentSections.length > 0 && onParentSectionChange && (
        <Box sx={{ mt: 1.5 }} data-testid="select-text-linked-section">
          <FormControl fullWidth size="small">
            <InputLabel>Linked to section</InputLabel>
            <Select
              value={parentSectionId ?? ''}
              label="Linked to section"
              onChange={(e: SelectChangeEvent) => {
                const val = e.target.value;
                onParentSectionChange(val || undefined);
              }}
              disabled={disabled}
            >
              <MenuItem value="">None</MenuItem>
              {availableParentSections.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
    </SAccordion>
  );
};
